import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PanelLeft, User, LogOut, MessageSquareHeart, Sun, Moon, Monitor } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useAuth } from '@/contexts/AuthContext';
import { useLiveConversation } from '@/hooks/useLiveConversation';
import { NotificationPanel } from '@/components/notifications/NotificationPanel';
import { UnreadsBell } from './UnreadsBell';
import { applyTheme, getTheme, type ThemeMode } from '@/lib/theme';
import { FeedbackDialog } from '@/components/FeedbackDialog';
import { cn } from '@/lib/utils';
import { BackLink } from '@/components/editorial';
import { isChatThreadRoute, isImmersiveRoute } from './immersive-routes';
import { useHeaderTitle } from './header-title';

/** Theme cycle for the header toggle (#768) — light → dark → system → light. */
const NEXT_THEME: Record<ThemeMode, ThemeMode> = {
  light: 'dark',
  dark: 'system',
  system: 'light',
};

interface HeaderProps {
  onMenuClick: () => void;
  onProfileClick: () => void;
  alwaysShowMenuButton?: boolean;
  /** Hide the hamburger entirely when there's no sidebar to open (e.g. applicant shell). */
  hideMenuButton?: boolean;
}

export function Header({
  onMenuClick,
  onProfileClick,
  alwaysShowMenuButton = false,
  hideMenuButton = false,
}: HeaderProps) {
  const { user, logout } = useAuth();
  const { enabled: liveConversation } = useLiveConversation();
  const { pathname } = useLocation();
  // Every visible word, tooltip and accessible name in the bar comes from
  // `shell.json` (#2109 L6).
  const { t } = useTranslation('shell');
  // Chat UX brief 2026-09-07, changes #3, #6 and #7: on the chat surface the
  // page collapses to ONE header row. The two hamburgers and the "Chat" label
  // go, and the thread's identity — "Talking with <persona>" — moves up here
  // beside the back arrow, where the page's own second row used to carry it.
  const immersive = isImmersiveRoute(pathname);
  const { title: pageTitle } = useHeaderTitle();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [theme, setTheme] = useState<ThemeMode>(getTheme);

  const handleThemeChange = (value: ThemeMode) => {
    setTheme(value);
    applyTheme(value);
  };
  // A literal key per mode, so the extractor can see all three and `t()`
  // stays typed; a template key would be invisible to both.
  const themeName = (mode: ThemeMode) =>
    mode === 'light' ? t('header.theme.light') : mode === 'dark' ? t('header.theme.dark') : t('header.theme.system');

  return (
    <header className="flex-shrink-0 z-50 w-full border-b border-stevens-rule-2 bg-stevens-bg pt-[env(safe-area-inset-top)]">
      {/* 3rem band (#1725; was 4rem): the strip holds icon buttons only, and
          the extra rem pushed every page's greeting down for nothing. The
          drawer no longer mirrors this value: since #2435 it is a
          full-height z-[60] overlay above this z-50 bar, because the
          language row above the bar makes its top edge move. */}
      <div className="flex h-12 items-center px-4 gap-2">
        {/* The rail toggle left this bar 2026-09-20: it is the panel icon in
            the sidebar's brand block (`SidebarBrand`), left of the bird. */}
        {/* Drawer trigger — the panel icon, matching the brand block's (hidden
            when there's no sidebar to open, and
            on the chat surface, where the brief asks for a single hamburger that
            lives in the conversation rail's icon row instead — change #3). */}
        {!hideMenuButton && !immersive && (
          <Button
            variant="ghost"
            size="icon"
            // Parents get the BottomNav "More" tab below sm, so their
            // hamburger hides there. Every other role (trfer/admin) has NO
            // bottom nav — this button is their only drawer trigger on
            // mobile, so it must stay visible (B-121).
            className={cn(
              'h-11 w-11 text-stevens-ink-2 hover:text-stevens-ink hover:bg-stevens-bg-2',
              user?.role === 'parent' && 'hidden sm:inline-flex',
              !alwaysShowMenuButton && 'md:hidden',
            )}
            onClick={onMenuClick}
            aria-label={t('header.openMenu')}
          >
            <PanelLeft className="h-4 w-4" />
          </Button>
        )}

        {/* Back — a LINK, never a history hop (design-grammar §4.2/§4.6, §6
            guard d). Since wave w1024 (2026-10-24) it returns to the in-app
            page this entry came from, with that page's filters (and its
            scroll, restored by MainLayout's tracker), labelled with its nav
            name; with no in-app origin — a deep link, a push, a fresh tab —
            it goes to the page's canonical parent, role-aware, with that
            list's last filters. `BackLink` renders nothing wherever there is
            no canonical parent — every role home, the wizard, re-consent —
            origin or not. The role goes in for the routes two shells share
            (#1456), for whether an origin is a page this hat can open, and
            for the fallback when the canonical parent is not. */}
        {/* Victor 2026-09-07: a thread's arrow must actually leave. It
            resolves against `/chat` on `/chat/:id`, so a thread goes to the
            home in one hop instead of landing on an empty chat page. #2266:
            `/chat` is no longer immersive (the thread lives on the home), so
            the rule keys off the thread route itself. */}
        <BackLink
          pathname={immersive || isChatThreadRoute(pathname) ? '/chat' : pathname}
          role={user?.role}
          label={immersive ? 'never' : 'responsive'}
        />

        {/* #7: the thread identity, in the top row beside the arrow. The name
            renders only once the family's persona has resolved — the loading
            state is deliberate (#1806), so the header never paints a default
            companion name and then swaps it. */}
        {immersive && pageTitle && (
          <h1 className="m-0 min-w-0 truncate text-card-title font-semibold leading-none tracking-[-0.01em] text-stevens-ink">
            {pageTitle}
          </h1>
        )}

        <div className="w-1" />

        {/* Spacer */}
        <div className="flex-1" />

        {/* Send feedback — icon-only like its siblings (ui-ux-guideline §2.8
            rule 6, 2026-09-18 Q12): the words live on aria-label and the
            tooltip. The desktop label and the Beta chip went with the
            indigo tint — app-bar controls are quieter than the page, and
            the 16px glyph in a 44px target is the shape every other control
            here already has.
            #2603: every icon button here is `shrink-0`. On /chat the thread
            title shares this row, and at 390 px the flex squeeze took the
            buttons down to 33 px wide; the title truncates instead. */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              aria-label={t('header.sendFeedback')}
              className="h-11 w-11 shrink-0 text-stevens-ink-2 hover:text-stevens-ink hover:bg-stevens-bg-2"
              onClick={() => setFeedbackOpen(true)}
            >
              <MessageSquareHeart className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('header.sendFeedback')}</TooltipContent>
        </Tooltip>

        {/* The bell. In the TRFer shell it IS the Unreads badge (§2.7, §2.8
            rule 4): the same number as the Unreads sidebar row, from the same
            endpoint, and pressing it opens Unreads. Parents and admins keep
            the notification popover. While the live conversation is dormant
            (#2566) the TRFer has no bell. */}
        {user?.role === 'trfer' ? (liveConversation ? <UnreadsBell /> : null) : <NotificationPanel />}

        {/* Profile — only when there is no sidebar to hold the profile row.
            #768 made the sidebar profile row the single profile entry point
            and removed the header avatar, but the applicant shell renders no
            sidebar (`hideMenuButton`), so without this the applicant would
            have no way to reach their profile at all. */}
        {hideMenuButton && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-11 w-11 shrink-0 text-stevens-ink-2 hover:text-stevens-ink hover:bg-stevens-bg-2"
                onClick={onProfileClick}
                aria-label={t('header.openProfile')}
              >
                <User className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{user?.name ? t('header.profileNamed', { name: user.name }) : t('header.profile')}</TooltipContent>
          </Tooltip>
        )}

        {/* Theme toggle (#768). Cycles light → dark → system rather than
            flipping two ways: the avatar dropdown this replaces offered all
            three, and a two-way toggle would silently delete "system" — a
            capability regression dressed as a simplification. The icon always
            shows the mode you are IN; the tooltip names the mode you will get.
            The canonical control is still Settings > Appearance (M2.8); this
            is the quick path to it. */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-11 w-11 shrink-0 text-stevens-ink-2 hover:text-stevens-ink hover:bg-stevens-bg-2"
              onClick={() => handleThemeChange(NEXT_THEME[theme])}
              aria-label={t('header.themeAria', { current: themeName(theme), next: themeName(NEXT_THEME[theme]) })}
            >
              {theme === 'light' && <Sun className="h-4 w-4" />}
              {theme === 'dark' && <Moon className="h-4 w-4" />}
              {theme === 'system' && <Monitor className="h-4 w-4" />}
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('header.themeSwitch', { next: themeName(NEXT_THEME[theme]) })}</TooltipContent>
        </Tooltip>

        {/* Sign out — icon only, no name (#768). "Sign out" is the ratified
            label and is kept as the accessible name (matches MobileRouteGuard),
            so the control is still findable by screen reader and by tooltip
            even though it renders as a bare glyph. */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-11 w-11 shrink-0 text-stevens-ink-2 hover:text-stevens-ink hover:bg-stevens-bg-2"
              onClick={() => logout()}
              aria-label={t('header.signOut')}
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('header.signOut')}</TooltipContent>
        </Tooltip>
      </div>

      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
    </header>
  );
}
